/*
 * rheincool — Kaufoptionen + Montage-Bundle (HANDOFF.md §7)
 *
 * Drei Optionen: nur-geraet | mit-montage | selbstmontage.
 * "mit-montage" legt ZWEI verbundene Line-Items in den Warenkorb:
 *   Gerät + Montage-Produkt, beide mit properties[_bundle_id],
 *   Montage zusätzlich mit properties[_parent] = Gerät-Variant-ID.
 * Unterstrich-Properties sind im Checkout unsichtbar.
 * Cart-Rendering gruppiert nach _bundle_id (cart-bundle.js / Cart-Templates).
 *
 * Zustand wird über pubsub ('rc:option-change') an Sticky-ATC durchgereicht.
 */
class RcPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.rc-options__card'));
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.atcButton = this.querySelector('[data-add-to-cart]');
    this.errorBox = this.querySelector('[data-atc-error]');
    this.warrantyRow = this.querySelector('[data-warranty-row]');
    this.warrantyToggle = this.querySelector('[data-warranty-toggle]');
    this.selected = 'nur-geraet';

    // Default laut Handoff §5: "Gerät + Montage" ist vorausgewählt, wenn verfügbar
    const featured = this.cards.find((c) => c.dataset.option === 'mit-montage');
    if (featured) this.selected = 'mit-montage';
    this.cards.forEach((c) => {
      const isSelected = c.dataset.option === this.selected;
      c.classList.toggle('is-selected', isSelected);
      c.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    if (this.warrantyToggle) {
      this.warrantyToggle.addEventListener('change', () => {
        if (window.rcTrack) {
          window.rcTrack('warranty_addon_toggle', { on: this.warrantyToggle.checked });
        }
        this.publishState();
      });
    }

    this.cards.forEach((card, index) => {
      card.addEventListener('click', () => this.select(card));
      card.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    const minus = this.querySelector('[data-qty-minus]');
    const plus = this.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', () => this.stepQty(-1));
    if (plus) plus.addEventListener('click', () => this.stepQty(1));

    if (this.atcButton) {
      this.atcButton.addEventListener('click', () => this.addToCart());
    }

    this.publishState();
  }

  /* Radiogroup: Pfeiltasten wechseln die Auswahl (WAI-ARIA radio pattern) */
  onKeydown(event, index) {
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const dir = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    const next = this.cards[(index + dir + this.cards.length) % this.cards.length];
    this.select(next);
    next.focus();
  }

  select(card) {
    this.cards.forEach((c) => {
      const isSelected = c === card;
      c.classList.toggle('is-selected', isSelected);
      c.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
    this.selected = card.dataset.option;
    if (window.rcTrack) window.rcTrack('purchase_option_select', { option: this.selected });
    this.publishState();
  }

  /* 1.299,– € bzw. 1.299,95 € — gleiche Darstellung wie rc-money.liquid */
  formatMoney(cents) {
    const euros = Math.floor(cents / 100);
    const rest = cents % 100;
    const grouped = new Intl.NumberFormat('de-DE').format(euros);
    return rest === 0 ? `${grouped},– €` : `${grouped},${String(rest).padStart(2, '0')} €`;
  }

  /* Garantie-Add-on hängt an Option 2: sonst abwählen + ausgrauen mit Hinweis */
  syncWarranty() {
    if (!this.warrantyRow || !this.warrantyToggle) return 0;
    const allowed = this.selected === 'mit-montage';
    if (!allowed && this.warrantyToggle.checked) this.warrantyToggle.checked = false;
    this.warrantyToggle.disabled = !allowed;
    this.warrantyRow.classList.toggle('is-disabled', !allowed);
    return this.warrantyToggle.checked ? parseInt(this.warrantyRow.dataset.warrantyPrice, 10) || 0 : 0;
  }

  /* Lieferumfang-Karte: Material/Montage-Zeilen folgen der gewählten Option */
  syncScopeCard() {
    const card = document.querySelector('[data-lieferumfang]');
    if (!card) return;
    const states = {
      material: this.selected === 'mit-montage' || this.selected === 'selbstmontage',
      montage: this.selected === 'mit-montage',
    };
    Object.entries(states).forEach(([key, included]) => {
      const row = card.querySelector(`[data-scope-row="${key}"]`);
      if (!row) return;
      row.classList.toggle('is-included', included);
      row.querySelector('.rc-scope-card__mark').textContent = included ? '✓' : '–';
      const on = row.querySelector('[data-scope-text-on]');
      const off = row.querySelector('[data-scope-text-off]');
      if (on) on.hidden = !included;
      if (off) off.hidden = included;
    });
  }

  publishState() {
    const card = this.cards.find((c) => c.dataset.option === this.selected);
    if (!card) return;
    const warrantyPrice = this.syncWarranty();
    this.syncScopeCard();
    const total = parseInt(card.dataset.price, 10) + warrantyPrice;
    const detail = {
      option: this.selected,
      label: card.dataset.label,
      price: total,
    };
    // Für Späteinsteiger (Sticky-ATC lädt nach dem ersten publish)
    window.rcOptionState = detail;
    this.dispatchEvent(new CustomEvent('rc:option-change', { bubbles: true, detail }));
    // Berater-Block: gewählte Option in die vorausgefüllte WhatsApp-Nachricht
    const wa = document.querySelector('[data-wa-link]');
    if (wa && wa.dataset.waBase && card.dataset.label) {
      const suffix = encodeURIComponent(` Option: ${card.dataset.label}.`);
      wa.href = `https://wa.me/${wa.dataset.waNumber}?text=${wa.dataset.waBase}${suffix}`;
    }
  }

  stepQty(delta) {
    const value = Math.min(10, Math.max(1, parseInt(this.qtyInput.value || '1', 10) + delta));
    this.qtyInput.value = value;
  }

  get quantity() {
    return Math.min(10, Math.max(1, parseInt(this.qtyInput.value || '1', 10)));
  }

  buildItems() {
    const qty = this.quantity;
    const baseVariant = parseInt(this.dataset.baseVariant, 10);

    if (this.selected === 'mit-montage' && this.dataset.montageVariant) {
      // Eindeutige Bundle-ID: Variant + Zeitstempel
      const bundleId = `${baseVariant}-${Date.now()}`;
      const items = [
        {
          id: baseVariant,
          quantity: qty,
          properties: { _bundle_id: bundleId },
        },
        {
          id: parseInt(this.dataset.montageVariant, 10),
          quantity: qty,
          properties: { _bundle_id: bundleId, _parent: String(baseVariant) },
        },
      ];
      // Garantie-Add-on nur mit Fachmontage (Handoff §5)
      if (this.warrantyToggle && this.warrantyToggle.checked && this.warrantyRow) {
        items.push({
          id: parseInt(this.warrantyRow.dataset.warrantyVariant, 10),
          quantity: qty,
          properties: { _bundle_id: bundleId, _parent: String(baseVariant) },
        });
      }
      return items;
    }

    if (this.selected === 'selbstmontage' && this.dataset.setVariant) {
      return [{ id: parseInt(this.dataset.setVariant, 10), quantity: qty }];
    }

    return [{ id: baseVariant, quantity: qty }];
  }

  async addToCart() {
    this.errorBox.hidden = true;
    this.atcButton.disabled = true;
    this.atcButton.classList.add('is-loading');

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: this.buildItems(),
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || 'add_failed');
      }

      this.renderSections(data.sections);
      this.showSuccess();
      if (window.rcTrack) {
        window.rcTrack('add_to_cart', { option: this.selected, quantity: this.quantity });
      }
      this.openDrawer();
    } catch (error) {
      this.errorBox.textContent = error.message === 'add_failed' ? this.errorBox.dataset.fallback || '' : error.message;
      this.errorBox.hidden = false;
    } finally {
      this.atcButton.disabled = false;
      this.atcButton.classList.remove('is-loading');
    }
  }

  renderSections(sections) {
    if (!sections) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      bubble.innerHTML = new DOMParser()
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .body.innerHTML;
      const countBubble = bubble.querySelector('.cart-count-bubble');
      if (countBubble) countBubble.classList.add('is-pulsing');
    }
    const drawer = document.querySelector('cart-drawer');
    if (drawer && sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const inner = parsed.querySelector('cart-drawer');
      if (inner) drawer.innerHTML = inner.innerHTML;
      drawer.classList.remove('is-empty');
    }
  }

  showSuccess() {
    // „✓ Hinzugefügt" (HANDOFF.md §10), danach zurück zum Normalzustand
    const original = this.atcButton.textContent;
    this.atcButton.classList.add('is-added');
    this.atcButton.textContent = this.atcButton.dataset.addedText || '✓';
    setTimeout(() => {
      this.atcButton.classList.remove('is-added');
      this.atcButton.textContent = original;
    }, 2000);
  }

  openDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open({ success: true });
  }
}

customElements.define('rc-purchase-options', RcPurchaseOptions);
